import React from 'react';
import { forgetRemoteRegistration } from './remoteRegistration';

/**
 * The retry behind the panel-sized failure state: a key to remount the
 * boundary, and a handler that makes the remount actually re-fetch.
 *
 * Both steps are needed, and the remount is the lesser one.
 * `@module-federation/runtime` memoises the load per module, so a remote that
 * failed keeps returning the same rejected promise however many times the
 * `React.lazy` under it is remounted — a retry would replay the original
 * failure forever, including after the remote came back up. Dropping the
 * registration first makes the next render re-register and re-fetch; the key
 * bump then remounts the boundary so the retried load is rendered.
 *
 * `remoteName` is optional because a caller may not have resolved its config
 * yet; the remount still happens, it just has nothing to un-cache.
 */
export function useCustomAppRetry(remoteName: string | undefined) {
  const [retryKey, setRetryKey] = React.useState(0);
  const onRetry = React.useCallback(() => {
    if (remoteName) {
      forgetRemoteRegistration(remoteName);
    }
    setRetryKey(k => k + 1);
  }, [remoteName]);
  return { retryKey, onRetry };
}
